import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, Star, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { BarArrayVisualizer, calculateScore } from '@code-arcade/engine'
import type { AlgorithmChallenge, BarArrayState, VisualizationStep } from '@code-arcade/engine'
import { bubbleSort, selectionSort, insertionSort, mergeSort, quickSort, quickselect, heapSort, countingSort, binarySearch } from '@content/algoquest'
import { AlgorithmCanvas } from '@/components/game/AlgorithmCanvas'
import { HUD } from '@/components/game/HUD'
import { StepControls } from '@/components/game/StepControls'
import { CutscenePanel } from '@/components/game/CutscenePanel'
import { CodePanel } from '@/components/game/CodePanel'
import { useGameStore } from '@/stores/game.store'
import { useAuthStore } from '@/stores/auth.store'
import { useProgressStore } from '@/stores/progress.store'
import { useMediaQuery } from '@/hooks/useMediaQuery'

const CHALLENGE_REGISTRY: Record<string, AlgorithmChallenge> = {
  'bubble-sort': bubbleSort,
  'selection-sort': selectionSort,
  'insertion-sort': insertionSort,
  'merge-sort': mergeSort,
  'quick-sort': quickSort,
  'quickselect': quickselect,
  'heap-sort': heapSort,
  'counting-sort': countingSort,
  'binary-search': binarySearch,
}

const STEP_GENERATORS: Record<string, (size: number) => VisualizationStep<BarArrayState>[]> = {
  'bubble-sort': (size) => BarArrayVisualizer.generateBubbleSortSteps(size),
  'selection-sort': (size) => BarArrayVisualizer.generateSelectionSortSteps(size),
  'insertion-sort': (size) => BarArrayVisualizer.generateInsertionSortSteps(size),
  'merge-sort': (size) => BarArrayVisualizer.generateMergeSortSteps(size),
  'quick-sort': (size) => BarArrayVisualizer.generateQuickSortSteps(size),
  'quickselect': (size) => BarArrayVisualizer.generateQuickselectSteps(size),
  'heap-sort': (size) => BarArrayVisualizer.generateHeapSortSteps(size),
  'counting-sort': (size) => BarArrayVisualizer.generateCountingSortSteps(size),
  'binary-search': (size) => BarArrayVisualizer.generateBinarySearchSteps(size),
}

const CHAPTER_LABELS: Record<string, string> = {
  'chapter-1': 'Chapter 1 · Innotek Systems Inc.',
  'chapter-2': 'Chapter 2 · Blink.io',
  'chapter-3': 'Chapter 3 · Blink.io',
  'chapter-4': 'Chapter 4 · Omnitech Corp',
  'chapter-5': 'Chapter 5 · ByteForge',
  'chapter-6': 'Chapter 6 · ByteForge → exit',
}

export default function Challenge() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { markCompleted, fetchProgress } = useProgressStore()
  const { startGame, setSteps, status, stepIndex, elapsedMs, resetGame } = useGameStore()
  const isDesktop = useMediaQuery('(min-width: 1024px)')

  // Ensure progress is loaded when navigating directly to a challenge
  useEffect(() => {
    if (user?.id) fetchProgress(user.id)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const challenge = id ? CHALLENGE_REGISTRY[id] : undefined

  const [panelOpen, setPanelOpen] = useState(false)
  const [sidebarTab, setSidebarTab] = useState<'info' | 'learn'>('info')
  const [activeInterrupt, setActiveInterrupt] = useState<{
    character: string
    mood: string
    message: string
  } | null>(null)
  const [completionResult, setCompletionResult] = useState<{
    stars: 1 | 2 | 3
    xpEarned: number
  } | null>(null)

  const generateSteps = useCallback((ch: AlgorithmChallenge) => {
    const gen = STEP_GENERATORS[ch.id]
    return gen ? gen(ch.visualization.defaultSize) : []
  }, [])

  useEffect(() => {
    if (!challenge) return
    resetGame()
    startGame(challenge.id)
    setSteps(generateSteps(challenge))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge?.id])

  useEffect(() => {
    if (!challenge?.interrupts) return
    const interrupt = challenge.interrupts.find((i) => i.atStep === stepIndex)
    if (interrupt) {
      useGameStore.getState().pauseGame()
      setActiveInterrupt(interrupt)
      setPanelOpen(true)
      setSidebarTab('info')
    }
  }, [stepIndex, challenge])

  const handleComplete = useCallback(() => {
    if (!challenge) return
    const result = calculateScore(elapsedMs, challenge.xpReward)
    setCompletionResult(result)
    if (user) {
      markCompleted(user.id, challenge.id, challenge.chapter, result.xpEarned, result.stars, elapsedMs)
    }
  }, [challenge, elapsedMs, user, markCompleted])

  // Desktop: slide from right. Mobile: slide from bottom.
  const panelVariants = isDesktop
    ? { hidden: { x: '100%' }, visible: { x: 0 }, exit: { x: '100%' } }
    : { hidden: { y: '100%' }, visible: { y: 0 }, exit: { y: '100%' } }

  if (!challenge) {
    return (
      <main className="min-h-screen bg-arcade-bg flex items-center justify-center text-white">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Challenge not found: <code>{id}</code></p>
          <button onClick={() => navigate(-1)} className="text-sm text-arcade-accent hover:underline">
            ← Go back
          </button>
        </div>
      </main>
    )
  }

  return (
    <div className="h-dvh bg-arcade-bg flex flex-col text-white overflow-hidden">
      {/* HUD */}
      <HUD
        algorithmTitle={challenge.title}
        chapterLabel={CHAPTER_LABELS[challenge.chapter] ?? challenge.chapter}
        panelOpen={panelOpen}
        onTogglePanel={() => setPanelOpen((v) => !v)}
      />

      {/* Back nav */}
      <div className="px-4 pt-3 pb-1">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-white transition-colors"
        >
          <ChevronLeft size={16} /> World Map
        </button>
      </div>

      {/* Canvas */}
      <div className="flex-1 relative min-h-0">
        {status !== 'idle' ? (
          <AlgorithmCanvas visualizationType={challenge.visualization.type as 'bar-array'} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-arcade-surface/10">
            <p className="text-gray-600 font-mono text-sm">Press Play to begin</p>
          </div>
        )}
      </div>

      {/* Step controls */}
      {status !== 'completed' && <StepControls onComplete={handleComplete} />}

      {/* ── Panel overlay ─────────────────────────────── */}
      <AnimatePresence>
        {panelOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/60 z-20 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setPanelOpen(false)}
            />

            <motion.aside
              className={[
                'fixed z-30 bg-arcade-surface flex flex-col overflow-hidden',
                'bottom-0 left-0 right-0 h-[92vh] rounded-t-2xl border-t border-arcade-border',
                'lg:left-auto lg:top-14 lg:bottom-0 lg:right-0 lg:w-[min(580px,45vw)] lg:h-auto lg:rounded-none lg:rounded-tl-2xl lg:border-l lg:border-t-0',
              ].join(' ')}
              variants={panelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            >
              <div className="lg:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
                <div className="w-10 h-1 rounded-full bg-gray-600" />
              </div>

              <div className="flex items-center border-b border-arcade-border flex-shrink-0">
                {(['info', 'learn'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setSidebarTab(tab)}
                    className={`flex-1 py-3 text-xs font-mono font-bold uppercase tracking-widest transition-colors ${
                      sidebarTab === tab
                        ? 'text-arcade-neon border-b-2 border-arcade-neon'
                        : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
                <button
                  onClick={() => setPanelOpen(false)}
                  className="px-4 py-3 text-gray-500 hover:text-white transition-colors"
                  aria-label="Close panel"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto overscroll-contain p-5 space-y-5">
                {/* ── Info tab ── */}
                {sidebarTab === 'info' && (
                  <>
                    <div>
                      <h2 className="font-bold text-white text-lg mb-1">{challenge.title}</h2>
                      <p className="text-sm text-gray-400 leading-relaxed">{challenge.description}</p>
                    </div>

                    <div className="flex gap-3">
                      <div className="bg-arcade-bg border border-arcade-border rounded-lg px-3 py-2.5 text-center flex-1">
                        <p className="text-xs text-gray-500 font-mono mb-1">Time</p>
                        <p className="text-sm font-mono font-bold text-arcade-neon">{challenge.complexity.time}</p>
                      </div>
                      <div className="bg-arcade-bg border border-arcade-border rounded-lg px-3 py-2.5 text-center flex-1">
                        <p className="text-xs text-gray-500 font-mono mb-1">Space</p>
                        <p className="text-sm font-mono font-bold text-arcade-neon">{challenge.complexity.space}</p>
                      </div>
                    </div>

                    <div className="bg-arcade-bg border border-arcade-border rounded-lg p-4 space-y-2">
                      <p className="text-xs font-mono text-gray-500">Real-world scenario</p>
                      <p className="text-sm text-gray-300 leading-relaxed">{challenge.realWorld.scenario}</p>
                      <p className="text-xs text-amber-400 leading-relaxed">⚠ {challenge.realWorld.consequence}</p>
                    </div>

                    {challenge.sampleDataset && (
                      <div className="bg-arcade-bg border border-arcade-border rounded-lg p-4 space-y-3">
                        <p className="text-xs font-mono text-gray-500">{challenge.sampleDataset.label}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {challenge.sampleDataset.preview.map((val, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-arcade-surface rounded text-xs font-mono text-arcade-neon border border-arcade-border"
                            >
                              {val.toLocaleString()}
                            </span>
                          ))}
                          <span className="px-2 py-1 text-xs font-mono text-gray-600">…</span>
                        </div>
                        <div className="border-t border-arcade-border pt-3 space-y-2">
                          <p className="text-xs font-mono text-gray-500">
                            At {challenge.sampleDataset.atScale.n.toLocaleString()} rows:
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-mono text-red-400">
                              {challenge.sampleDataset.atScale.leftLabel ?? 'Bubble Sort'}
                            </span>
                            <span className="text-xs font-mono font-bold text-red-300">
                              {challenge.sampleDataset.atScale.leftNote}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-mono text-arcade-neon">
                              {challenge.sampleDataset.atScale.rightLabel ?? 'Array.sort()'}
                            </span>
                            <span className="text-xs font-mono font-bold text-arcade-neon">
                              {challenge.sampleDataset.atScale.rightNote}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeInterrupt && (
                      <CutscenePanel
                        frames={[{
                          character: activeInterrupt.character,
                          mood: activeInterrupt.mood,
                          dialogue: activeInterrupt.message,
                        }]}
                        allowSkip={false}
                        onComplete={() => {
                          setActiveInterrupt(null)
                          setPanelOpen(false)
                          useGameStore.getState().resumeGame()
                        }}
                      />
                    )}
                  </>
                )}

                {/* ── Learn tab ── */}
                {sidebarTab === 'learn' && (
                  <>
                    {challenge.keyTakeaway && (
                      <div className="bg-arcade-neon/5 border border-arcade-neon/20 rounded-lg p-4">
                        <p className="text-xs font-mono text-arcade-neon mb-2">Key takeaway</p>
                        <p className="text-sm text-gray-200 leading-relaxed">{challenge.keyTakeaway}</p>
                      </div>
                    )}

                    {(challenge.whenToUse || challenge.whenNotToUse) && (
                      <div className="space-y-4">
                        {challenge.whenToUse && (
                          <div>
                            <p className="text-xs font-mono text-gray-500 mb-2">✅ Use it when</p>
                            <ul className="space-y-2">
                              {challenge.whenToUse.map((item, i) => (
                                <li key={i} className="text-xs text-gray-300 leading-relaxed flex gap-2">
                                  <span className="text-arcade-neon flex-shrink-0 mt-0.5">›</span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {challenge.whenNotToUse && (
                          <div>
                            <p className="text-xs font-mono text-gray-500 mb-2">❌ Don't use it when</p>
                            <ul className="space-y-2">
                              {challenge.whenNotToUse.map((item, i) => (
                                <li key={i} className="text-xs text-gray-300 leading-relaxed flex gap-2">
                                  <span className="text-red-400 flex-shrink-0 mt-0.5">›</span>
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {(challenge.pseudocode || challenge.codeSnippets) && (
                      <div>
                        <p className="text-xs font-mono text-gray-500 mb-2">The code</p>
                        <CodePanel
                          pseudocode={challenge.pseudocode}
                          snippets={challenge.codeSnippets}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Completion overlay ── */}
      {completionResult && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4">
          <div className="bg-arcade-surface border border-arcade-border rounded-2xl p-8 w-full max-w-sm text-center relative">
            <button
              onClick={() => setCompletionResult(null)}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800 transition-colors"
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>

            <p className="text-sm font-mono text-gray-500 mb-2">Challenge complete</p>
            <h2 className="text-2xl font-bold text-white mb-4">{challenge.title}</h2>

            <div className="flex justify-center gap-2 mb-4">
              {[1, 2, 3].map((n) => (
                <Star
                  key={n}
                  size={32}
                  className={n <= completionResult.stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-700 fill-gray-700'}
                />
              ))}
            </div>

            <div className="bg-arcade-bg rounded-xl px-6 py-3 mb-6 inline-block">
              <p className="text-xs text-gray-500 font-mono">XP earned</p>
              <p className="text-3xl font-mono font-bold text-arcade-neon">+{completionResult.xpEarned}</p>
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate(-1)}
                className="w-full py-3 bg-arcade-accent hover:bg-violet-500 text-white font-bold rounded-xl transition-colors"
              >
                Back to World Map
              </button>
              <button
                onClick={() => {
                  setCompletionResult(null)
                  resetGame()
                  setSteps(generateSteps(challenge))
                  startGame(challenge.id)
                }}
                className="w-full py-2.5 text-gray-400 hover:text-white text-sm transition-colors"
              >
                Play again
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
